import * as React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MailCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { api, ApiError } from '../api-client';
import { WorqlyMark } from '../../design-system/components/WorqlyMark';
import { TurnstileWidget } from '../../marketing/components/TurnstileWidget';

export function ForgotPasswordPage() {
  const [email, setEmail] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = React.useState('');
  const [widgetEpoch, setWidgetEpoch] = React.useState(0);

  const sentHeadingRef = React.useRef<HTMLHeadingElement | null>(null);
  React.useEffect(() => {
    if (sent) sentHeadingRef.current?.focus();
  }, [sent]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api('/api/auth/forgot-password', {
        method: 'POST',
        body: { email, turnstileToken },
        noRedirectOn401: true,
      });
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'captcha_failed') {
          setError('Captcha verification failed — please try again.');
          setTurnstileToken('');
          setWidgetEpoch((n) => n + 1);
        } else if (err.code === 'rate_limited') {
          setError('Too many requests from your IP — please wait a few minutes and try again.');
        } else {
          setError(err.message);
        }
      } else {
        setError(String((err as any)?.message ?? err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen flex flex-col">
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 bg-pds-mesh opacity-95" />
      <main className="flex-1 flex items-center justify-center px-6">

      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink/65 hover:text-ink dark:text-white/70 dark:hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to sign in
        </Link>

        <WorqlyMark size="md" />

        {sent ? (
          <div className="mt-8">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-coral/10 text-coral">
              <MailCheck className="h-6 w-6" />
            </div>
            <h1
              ref={sentHeadingRef}
              tabIndex={-1}
              className="mt-4 text-2xl font-light text-ink dark:text-white tracking-[-0.025em] focus:outline-none"
            >
              Check your inbox.
            </h1>
            <p className="mt-2 text-[14px] text-ink/60 dark:text-white/60">
              If an account exists for{' '}
              <strong className="text-ink dark:text-white">{email}</strong>, we've emailed a link to
              set a new password. The link expires in 15 minutes.
            </p>
            <p className="mt-4 text-[13px] text-ink/50 dark:text-white/50">
              Nothing arriving? Check spam, or{' '}
              <Link to="/contact" className="underline underline-offset-2">
                contact support
              </Link>
              .
            </p>
          </div>
        ) : (
          <div className="mt-8">
            <p className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3">
              Password reset
            </p>
            <h1 className="font-light text-display-md text-ink dark:text-white tracking-[-0.025em] leading-[1.05]">
              Forgot your password?
            </h1>
            <p className="text-[14.5px] text-ink/60 dark:text-white/60 mt-3 mb-8">
              Enter the email you sign in with and we'll send you a link to set a new one.
            </p>

            <div className="rounded-3xl bg-white/70 dark:bg-white/[0.06] backdrop-blur-xl ring-1 ring-ink/[0.08] dark:ring-white/10 shadow-card p-7">
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="fp-email" className="text-[12px] font-semibold text-ink/70 dark:text-white/70">
                    Email
                  </Label>
                  <Input
                    id="fp-email"
                    type="email"
                    required
                    autoFocus
                    autoComplete="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <TurnstileWidget
                  key={widgetEpoch}
                  onToken={setTurnstileToken}
                  onExpire={() => setTurnstileToken('')}
                />

                {error && (
                  <div
                    className="text-[13px] text-rose-700 dark:text-rose-300 bg-rose-500/10 ring-1 ring-rose-500/20 rounded-2xl px-3 py-2"
                    role="alert"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={submitting || !email || !turnstileToken}
                >
                  {submitting ? 'Sending…' : 'Send reset link'}
                </Button>
              </form>
            </div>
          </div>
        )}
      </div>
      </main>
    </div>
  );
}
